import { AxiosResponse } from 'axios'
import { handleResponseError } from 'helpers/utils'
import { API } from './endpoint'
import httpClient from '../axios/interceptors'
import {
  FilterTrainingInput,
  RegisterRoundInput,
  ResponseMyBookings,
  ResponseRegisterRound,
  ResponseTrainingInvitations,
  ResponseTrainingDetail,
  ResponseTrainingList,
  ResponseTrainingRounds,
} from './interface/eventCalendar/eventCalendar.interface'

/** error ที่ backend ส่งมาเมื่อบัญชียังไม่มีอีเมล */
export const EMAIL_REQUIRED_CODE = 'EMAIL_REQUIRED'

/**
 * เช็คว่า error ที่ได้คือ "ยังไม่มีอีเมลในโปรไฟล์"
 * NestJS ห่อ object ที่โยนจาก BadRequestException ไว้ใน response body
 * (ผ่าน HttpExceptionFilter ของโปรเจกต์) จึงเช็คหลายชั้นเผื่อไว้
 */
export const isEmailRequiredError = (error: any): boolean => {
  const body = error?.response?.data

  return (
    body?.code === EMAIL_REQUIRED_CODE ||
    body?.message?.code === EMAIL_REQUIRED_CODE ||
    body?.description?.code === EMAIL_REQUIRED_CODE ||
    (typeof body?.description === 'string' && body.description.includes(EMAIL_REQUIRED_CODE)) ||
    (typeof body?.message === 'string' && body.message.includes(EMAIL_REQUIRED_CODE))
  )
}

export const getTrainingList = async (input: FilterTrainingInput): Promise<ResponseTrainingList> => {
  const data: AxiosResponse<ResponseTrainingList> = await httpClient
    .post(API.factoryEventCalendarFind, input)
    .catch(error => handleResponseError(error))

  return data.data
}

export const getTrainingById = async (eventId: string): Promise<ResponseTrainingDetail> => {
  const data: AxiosResponse<ResponseTrainingDetail> = await httpClient
    .get(API.factoryEventCalendarById + eventId)
    .catch(error => handleResponseError(error))

  return data.data
}

export const getTrainingRounds = async (eventId: string): Promise<ResponseTrainingRounds> => {
  const data: AxiosResponse<ResponseTrainingRounds> = await httpClient
    .get(API.factoryEventCalendarRounds(eventId))
    .catch(error => handleResponseError(error))

  return data.data
}

/**
 * จองรอบอบรม
 *
 * ไม่ส่งอีเมลไปด้วย — server อ่านจาก user.email เอง ถ้าโปรไฟล์ยังไม่มีอีเมล
 * จะได้ 400 code EMAIL_REQUIRED (ใช้ isEmailRequiredError ตรวจ)
 *
 * ห้ามใส่ handleResponseError ที่นี่ เพราะจะกลืน error ทำให้ฝั่ง UI
 * แยกเคส EMAIL_REQUIRED / รอบเต็ม ไม่ได้ — ปล่อยให้ throw ขึ้นไปให้ hook จัดการ
 */
export const registerTrainingRound = async (
  input: RegisterRoundInput
): Promise<ResponseRegisterRound> => {
  const { event_id, ...body } = input
  const data: AxiosResponse<ResponseRegisterRound> = await httpClient.post(
    API.factoryEventCalendarRegister(event_id),
    body
  )

  return data.data
}

/**
 * คำเชิญอบรมที่ยังรอตอบ
 *
 * ร้านที่ไม่ได้มาจากลีดได้ `data: []` เป็นคำตอบปกติ ไม่ต้องดักเป็น error
 */
export const getTrainingInvitations = async (): Promise<ResponseTrainingInvitations> => {
  const data: AxiosResponse<ResponseTrainingInvitations> = await httpClient
    .get(API.factoryEventCalendarInvitations)
    .catch(error => handleResponseError(error))

  return data.data
}

export const cancelTrainingBooking = async (attendeeId: string): Promise<void> => {
  await httpClient
    .post(API.factoryEventCalendarCancel, { attendee_id: attendeeId })
    .catch(error => handleResponseError(error))
}

export const getMyTrainingBookings = async (input?: {
  date_from?: string
  date_to?: string
  upcoming_only?: boolean
}): Promise<ResponseMyBookings> => {
  const data: AxiosResponse<ResponseMyBookings> = await httpClient
    .post(API.factoryEventCalendarMyBookings, input ?? {})
    .catch(error => handleResponseError(error))

  return data.data
}
